import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { AliasService, AliasVendor } from '../../services/alias.service';
import { AliasSessionStarter } from '../../services/alias-session-starter.service';
import { serverErrorMessage } from '../../helpers/api-error.util';

/** The args every fetch (search-typing or page-change) carries into the unified fetch path. */
interface AliasVendorFetch {
  term: string;
  page: number;
  size: number;
}

/**
 * Staff picker for starting a read-only alias session. Opened from the
 * header's "Alias User" menu item (internal + `canAlias` only). Loads active
 * PAs server-side (paged, via `AliasService.searchVendors`) — page 0 with a
 * blank term on open, so the staffer sees all active PAs without typing — lets
 * the staffer search (debounced) and page through the results, select one, and
 * starts the session on Confirm.
 *
 * Both a search-term change and a paginator click flow through one `fetch$`
 * trigger + `switchMap`, so the HTTP call and result assignment can't diverge
 * and the latest request always wins (a page click mid-search cancels the
 * pending search and vice-versa). Typing is debounced (300ms) upstream of
 * `fetch$`; page changes fetch immediately.
 *
 * `confirm()` delegates the actual `startSession -> re-confirm /user/me`
 * orchestration to `AliasSessionStarter` (shared with the Management -> Users
 * row button) and, on success, performs the app's hard reload itself (see
 * `reloadIntoVendorPortal`) — see that service's class doc for why `/me` is
 * re-run before the reload. That reload is extracted into its own method —
 * rather than inlined, as `AccountSideNavComponent.logout()` does — so a spec
 * can `spyOn` it: Chrome Headless makes `window.location` non-configurable,
 * so triggering a real reload mid-test-run would corrupt the Karma run (see
 * account-side-nav.component.spec.ts for the same tradeoff).
 */
@Component({
  standalone: true,
  selector: 'app-alias-vendor-picker-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
  ],
  templateUrl: './alias-vendor-picker-dialog.component.html',
  styleUrl: './alias-vendor-picker-dialog.component.scss',
})
export class AliasVendorPickerDialogComponent implements OnInit, OnDestroy {
  searchTerm = '';
  results: AliasVendor[] = [];
  selected: AliasVendor | null = null;
  searching = false;
  starting = false;
  /** True once a fetch has resolved at least once, so the empty-state message only appears after a real fetch. */
  searched = false;

  /** Server-side paging state (0-based `page`); `total` is the full filtered count that drives the paginator length. */
  page = 0;
  pageSize = 50;
  total = 0;

  @ViewChild(MatPaginator) paginator?: MatPaginator;

  private readonly searchChange$ = new Subject<string>();
  private readonly fetch$ = new Subject<AliasVendorFetch>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<AliasVendorPickerDialogComponent>,
    private aliasService: AliasService,
    private aliasSessionStarter: AliasSessionStarter,
    private toastr: ToastrService
  ) {
    // Typing is debounced, then always resets to page 0 before joining the
    // unified fetch path. Deliberately no distinctUntilChanged: a
    // cleared-then-repeated term (e.g. "acme" -> clear -> "acme") must still
    // re-run rather than being suppressed as a stale "unchanged" value.
    this.searchChange$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((term) => this.fetch$.next({ term, page: 0, size: this.pageSize }));

    // The single fetch path shared by search-typing and paginator clicks.
    this.fetch$
      .pipe(
        switchMap(({ term, page, size }) => {
          this.searching = true;
          // Catch INSIDE the inner observable (not on the outer subscribe's
          // `error` callback) — an error there would complete the whole
          // pipeline, leaving every later fetch$.next() a no-op for the rest of
          // the dialog's life. Recovering to an empty result set keeps the
          // stream alive so the next search/page still runs.
          return this.aliasService.searchVendors(term, page, size).pipe(
            catchError(() => {
              this.toastr.error('Failed to search vendors.');
              return of({ items: [], total: 0, page, size });
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((result) => {
        this.results = result?.items ?? [];
        this.total = result?.total ?? 0;
        // Re-sync page from the response that actually resolved. A debounced
        // search (page 0) can supersede an in-flight page-click via switchMap;
        // without this, `this.page` would keep the clicked index and the
        // paginator would render e.g. "51–100" over the page-0 rows that landed.
        this.page = result?.page ?? this.page;
        this.searching = false;
        this.searched = true;
      });
  }

  ngOnInit(): void {
    // Load-on-open: page 0 with a blank term shows all active PAs. The dialog
    // opens fresh each time, so this one-shot kick-off is enough.
    this.fetch$.next({ term: '', page: 0, size: this.pageSize });
  }

  /** Bound to the search input's `(ngModelChange)`. Resets to page 0 (blank term shows all) and refetches (debounced). */
  onSearchChange(): void {
    this.selected = null;
    this.page = 0;
    // `this.page` is the source of truth for the fetch; firstPage() only resyncs
    // the paginator UI (and is a no-op when already on page 0). Guarded because
    // the paginator sits behind *ngIf and can be transiently absent.
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.searchChange$.next(this.searchTerm.trim());
  }

  /** Paginator `(page)` handler — refetch the current term at the new page/size immediately (no debounce). */
  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetch$.next({ term: this.searchTerm.trim(), page: this.page, size: this.pageSize });
  }

  select(vendor: AliasVendor): void {
    this.selected = vendor;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (!this.selected || this.starting) {
      return;
    }
    this.starting = true;
    this.aliasSessionStarter.start(this.selected.id).subscribe({
      next: () => this.reloadIntoVendorPortal(),
      error: (err) => {
        this.starting = false;
        this.toastr.error(serverErrorMessage(err, 'Failed to start the alias session.'));
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Hard reload so the whole app re-bootstraps as the vendor (see class doc).
   * Lands on `/dashboard` — the PA landing route, gated by `AuthGuard` +
   * `RoleGuard` — rather than `/` (root), whose guard reads the cached user
   * synchronously and would still see the staff's stale INTERNAL role at this
   * instant. By the time this runs, `confirm()` has already re-confirmed
   * `/user/me` (see class doc), so `RoleGuard`'s synchronous read of
   * `getStoredUser()` on the fresh reload sees the aliased VENDOR, not the
   * staffer.
   */
  protected reloadIntoVendorPortal(): void {
    window.location.href = '/dashboard';
  }
}
